import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Row, Col, Card, Button, Tag, Modal, Form, Input, InputNumber, Select, Upload,
  Spin, Typography, Space, Timeline, Badge, Tooltip, Popconfirm, Image
} from 'antd';
import {
  PlusOutlined, CommentOutlined, PaperClipOutlined, ClockCircleOutlined,
  UploadOutlined, DeleteOutlined, LockOutlined, HourglassOutlined,
  BugOutlined, RocketOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { getAuthFileUrl } from '../utils/fileUrl';
import {
  canCreatePrivateBoardTask,
  canDeleteBoardTask,
  canEditBoardTaskHours,
  canSeeBoardTaskComments,
} from '../modules/board/boardAccessPolicy';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';

const { TextArea } = Input;
const { Text, Title } = Typography;

interface BoardTask {
  id: string;
  taskNumber: number;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  visibility: string;
  taskType: string | null;
  createdById: string;
  createdByName: string;
  attachments: any[];
  hoursSpent: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

interface BoardComment {
  id: string;
  taskId: string;
  authorId: string | null;
  authorName: string;
  content: string;
  attachments: any[];
  createdAt: string;
}

interface BoardActivity {
  id: string;
  taskId: string;
  actorName: string;
  action: string;
  details: any;
  createdAt: string;
}

const STATUSES = ['NEW', 'IN_PROGRESS', 'REVIEW', 'DONE', 'CANCELLED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const STATUS_COLORS: Record<string, string> = {
  NEW: 'default',
  IN_PROGRESS: 'processing',
  REVIEW: 'warning',
  DONE: 'success',
  CANCELLED: 'error'
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'default',
  MEDIUM: 'blue',
  HIGH: 'orange',
  URGENT: 'red'
};

const COLUMN_COLORS: Record<string, string> = {
  NEW: '#d9d9d9',
  IN_PROGRESS: '#1890ff',
  REVIEW: '#faad14',
  DONE: '#52c41a',
  CANCELLED: '#ff4d4f'
};

const Board: React.FC = () => {
  const { t } = useTranslation(['board', 'common']);
  const feedback = useFeedback();
  const [tasks, setTasks] = useState<BoardTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<(BoardTask & { comments: BoardComment[]; activities: BoardActivity[] }) | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const user = authService.getCurrentUser();
  const canEditHours = canEditBoardTaskHours(user);
  const canSeeComments = canSeeBoardTaskComments(user);
  const canCreatePrivate = canCreatePrivateBoardTask(user);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await api.get('/board/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch board tasks:', error);
      feedback.error(getApiErrorMessage(error, t, 'board:errors.fetchTasksFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const tasksByStatus = useMemo(() => {
    const grouped: Record<string, BoardTask[]> = {};
    STATUSES.forEach(s => { grouped[s] = []; });
    tasks.forEach(task => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });
    return grouped;
  }, [tasks]);

  const handleCreateTask = async (values: any) => {
    setSubmitting(true);
    try {
      const attachments = (values.attachments || [])
        .filter((f: any) => f.status === 'done' && f.response)
        .map((f: any) => ({
          url: f.response.url,
          filename: f.response.filename,
          size: f.response.size,
          uploadedAt: new Date().toISOString()
        }));

      const response = await api.post('/board/tasks', {
        title: values.title,
        description: values.description,
        priority: values.priority || 'MEDIUM',
        visibility: values.visibility || 'PUBLIC',
        attachments
      });
      feedback.success(t('board:messages.createSuccess', { taskNumber: response.data.taskNumber, title: values.title }));
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchTasks();
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'board:errors.createTaskFailed', undefined, { title: values.title })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openTaskDetail = async (taskId: string) => {
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const response = await api.get(`/board/tasks/${taskId}`);
      setSelectedTask(response.data);
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'board:errors.loadTaskFailed'));
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    const taskNumber = tasks.find((task) => task.id === taskId)?.taskNumber;
    const statusLabel = t(`board:statuses.${newStatus}`);
    try {
      await api.patch(`/board/tasks/${taskId}/status`, { status: newStatus });
      feedback.success(t('board:messages.statusChanged', { taskNumber, status: statusLabel }));
      fetchTasks();
      if (selectedTask?.id === taskId) {
        openTaskDetail(taskId);
      }
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'board:errors.statusChangeFailed', undefined, { taskNumber })
      );
    }
  };

  const handleAddComment = async (values: any) => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const attachments = (values.commentAttachments || [])
        .filter((f: any) => f.status === 'done' && f.response)
        .map((f: any) => ({
          url: f.response.url,
          filename: f.response.filename,
          size: f.response.size,
          uploadedAt: new Date().toISOString()
        }));

      await api.post(`/board/tasks/${selectedTask.id}/comments`, {
        content: values.content,
        attachments
      });
      feedback.success(t('board:messages.commentAdded', { taskNumber: selectedTask.taskNumber }));
      commentForm.resetFields();
      openTaskDetail(selectedTask.id);
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'board:errors.commentFailed', undefined, { taskNumber: selectedTask.taskNumber })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleHoursChange = async (taskId: string, hours: number | null) => {
    if (hours === null || hours < 0) return;
    const taskNumber = tasks.find((task) => task.id === taskId)?.taskNumber ?? selectedTask?.taskNumber;
    try {
      await api.put(`/board/tasks/${taskId}`, { hoursSpent: hours });
      feedback.success(t('board:messages.hoursUpdated', { taskNumber, hours }));
      fetchTasks();
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, hoursSpent: hours });
      }
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'board:errors.hoursUpdateFailed', undefined, { taskNumber })
      );
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const taskNumber = tasks.find((task) => task.id === taskId)?.taskNumber ?? selectedTask?.taskNumber;
    try {
      await api.delete(`/board/tasks/${taskId}`);
      feedback.success(t('board:messages.deleteSuccess', { taskNumber }));
      setDetailModalVisible(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'board:errors.deleteTaskFailed', undefined, { taskNumber }));
    }
  };

  const uploadProps = {
    action: `${api.defaults.baseURL}/upload`,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    accept: '.jpg,.jpeg,.png',
    listType: 'picture' as const,
    maxCount: 5
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>{t('board:title')}</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          {t('board:newTask')}
        </Button>
      </div>

      {/* Kanban Columns */}
      <Row gutter={12} style={{ overflowX: 'auto', flexWrap: 'nowrap', minHeight: 'calc(100vh - 200px)' }}>
        {STATUSES.map(status => (
          <Col key={status} style={{ minWidth: 280, flex: '1 1 0' }}>
            <Card
              size="small"
              title={
                <Space>
                  <Badge color={COLUMN_COLORS[status]} />
                  <span>{t(`board:columns.${status}`)}</span>
                  <Tag>{tasksByStatus[status].length}</Tag>
                </Space>
              }
              style={{ height: '100%', backgroundColor: '#fafafa' }}
              bodyStyle={{ padding: 8 }}
            >
              {tasksByStatus[status].length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#bbb' }}>
                  <Text type="secondary">{t('board:noTasks')}</Text>
                </div>
              ) : (
                tasksByStatus[status].map(task => (
                  <Card
                    key={task.id}
                    size="small"
                    hoverable
                    onClick={() => openTaskDetail(task.id)}
                    style={{ marginBottom: 8, cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Space size={4}>
                        <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>#{task.taskNumber}</Text>
                        <Tag color={PRIORITY_COLORS[task.priority]}>
                          {t(`board:priorities.${task.priority}`)}
                        </Tag>
                        {task.taskType === 'BUG' && (
                          <Tag color="red" icon={<BugOutlined />}>BUG</Tag>
                        )}
                        {task.taskType === 'FEATURE' && (
                          <Tag color="green" icon={<RocketOutlined />}>FEATURE</Tag>
                        )}
                      </Space>
                      {task.visibility === 'PRIVATE' && (
                        <Tooltip title={t('board:visibilityOptions.privateHint')}>
                          <LockOutlined style={{ color: '#999' }} />
                        </Tooltip>
                      )}
                    </div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      {task.title}
                    </Text>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {task.createdByName}
                      </Text>
                      <Space size={8}>
                        {task.attachments && task.attachments.length > 0 && (
                          <Tooltip title={t('board:attachments')}>
                            <PaperClipOutlined style={{ color: '#999' }} />
                          </Tooltip>
                        )}
                        {canSeeComments && task.commentCount > 0 && (
                          <Tooltip title={t('board:comments')}>
                            <Space size={2}>
                              <CommentOutlined style={{ color: '#999' }} />
                              <Text type="secondary" style={{ fontSize: 12 }}>{task.commentCount}</Text>
                            </Space>
                          </Tooltip>
                        )}
                        {task.hoursSpent > 0 && (
                          <Tooltip title={t('board:hoursSpent')}>
                            <Space size={2}>
                              <HourglassOutlined style={{ color: '#999' }} />
                              <Text type="secondary" style={{ fontSize: 12 }}>{task.hoursSpent}h</Text>
                            </Space>
                          </Tooltip>
                        )}
                      </Space>
                    </div>
                  </Card>
                ))
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Create Task Modal */}
      <Modal
        title={t('board:newTask')}
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); createForm.resetFields(); }}
        maskClosable={false}
        footer={null}
        width={600}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateTask} onFinishFailed={() => feedback.warning(t('board:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="title"
            label={t('board:taskTitle')}
            rules={[{ required: true, message: t('board:validation.titleRequired') }]}
          >
            <Input placeholder={t('board:taskTitlePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('board:taskDescription')}>
            <TextArea rows={4} placeholder={t('board:taskDescriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label={t('board:priority')} initialValue="MEDIUM">
                <Select>
                  {PRIORITIES.map(p => (
                    <Select.Option key={p} value={p}>
                      <Tag color={PRIORITY_COLORS[p]}>{t(`board:priorities.${p}`)}</Tag>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {canCreatePrivate && (
              <Col span={12}>
                <Form.Item name="visibility" label={t('board:visibility')} initialValue="PUBLIC">
                  <Select>
                    <Select.Option value="PUBLIC">{t('board:visibilityOptions.PUBLIC')}</Select.Option>
                    <Select.Option value="PRIVATE">
                      <Space>
                        <LockOutlined />
                        {t('board:visibilityOptions.PRIVATE')}
                      </Space>
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item
            name="attachments"
            label={t('board:attachments')}
            valuePropName="fileList"
            getValueFromEvent={(e: any) => e?.fileList}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>{t('board:uploadImage')}</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCreateModalVisible(false); createForm.resetFields(); }}>
                {t('common:cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {t('board:newTask')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Detail Modal */}
      <Modal
        title={selectedTask ? `#${selectedTask.taskNumber} — ${selectedTask.title}` : t('board:taskDetail')}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setSelectedTask(null); }}
        maskClosable={false}
        footer={null}
        width={700}
      >
        {detailLoading ? (
          <Spin style={{ display: 'block', margin: '40px auto' }} />
        ) : selectedTask ? (
          <div>
            {/* Status + Priority + Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Space>
                <Tag color={STATUS_COLORS[selectedTask.status]}>
                  {t(`board:statuses.${selectedTask.status}`)}
                </Tag>
                <Tag color={PRIORITY_COLORS[selectedTask.priority]}>
                  {t(`board:priorities.${selectedTask.priority}`)}
                </Tag>
                {selectedTask.taskType === 'BUG' && (
                  <Tag color="red" icon={<BugOutlined />}>BUG</Tag>
                )}
                {selectedTask.taskType === 'FEATURE' && (
                  <Tag color="green" icon={<RocketOutlined />}>FEATURE</Tag>
                )}
                {selectedTask.visibility === 'PRIVATE' && (
                  <Tag icon={<LockOutlined />}>{t('board:visibilityOptions.PRIVATE')}</Tag>
                )}
              </Space>
              <Space>
                <Select
                  value={selectedTask.status}
                  onChange={(val) => handleStatusChange(selectedTask.id, val)}
                  style={{ width: 150 }}
                  size="small"
                >
                  {STATUSES.map(s => (
                    <Select.Option key={s} value={s}>
                      {t(`board:statuses.${s}`)}
                    </Select.Option>
                  ))}
                </Select>
                {canDeleteBoardTask(selectedTask, user) && (
                  <Popconfirm
                    title={t('board:deleteConfirm')}
                    onConfirm={() => { void handleDeleteTask(selectedTask.id); }}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Space>
            </div>

            {/* Description */}
            {selectedTask.description && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedTask.description}</Text>
              </Card>
            )}

            {/* Attachments */}
            {selectedTask.attachments && selectedTask.attachments.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  <PaperClipOutlined /> {t('board:attachments')}
                </Text>
                <Image.PreviewGroup>
                  <Space wrap>
                    {selectedTask.attachments.map((att: any, i: number) => (
                      <Image
                        key={i}
                        src={getAuthFileUrl(att.url)}
                        alt={att.filename}
                        width={120}
                        height={80}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            )}

            {/* Hours Spent */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HourglassOutlined style={{ color: '#999' }} />
              <Text type="secondary">{t('board:hoursSpent')}:</Text>
              {canEditHours ? (
                <InputNumber
                  size="small"
                  min={0}
                  step={0.5}
                  value={selectedTask.hoursSpent || 0}
                  onChange={(val) => handleHoursChange(selectedTask.id, val)}
                  style={{ width: 80 }}
                  suffix="h"
                />
              ) : (
                <Text strong>{selectedTask.hoursSpent || 0}h</Text>
              )}
            </div>

            {/* Meta */}
            <div style={{ marginBottom: 16, color: '#999', fontSize: 12 }}>
              <Space split="·">
                <span>{t('board:createdBy')}: {selectedTask.createdByName}</span>
                <span>{formatDate(selectedTask.createdAt)}</span>
              </Space>
            </div>

            {/* Comments — only visible to TESTER */}
            {canSeeComments && (
              <>
                <Title level={5} style={{ marginBottom: 12 }}>
                  <CommentOutlined /> {t('board:comments')}
                </Title>

                {selectedTask.comments.length === 0 ? (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    {t('board:noComments')}
                  </Text>
                ) : (
                  <div style={{ marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
                    {selectedTask.comments.map(comment => (
                      <Card key={comment.id} size="small" style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 13 }}>{comment.authorName}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{formatDate(comment.createdAt)}</Text>
                        </div>
                        <Text style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</Text>
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <Image.PreviewGroup>
                              <Space wrap>
                                {comment.attachments.map((att: any, i: number) => (
                                  <Image
                                    key={i}
                                    src={getAuthFileUrl(att.url)}
                                    alt={att.filename}
                                    width={80}
                                    height={60}
                                    style={{ objectFit: 'cover', borderRadius: 4 }}
                                  />
                                ))}
                              </Space>
                            </Image.PreviewGroup>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}

                {/* Add Comment Form */}
                <Form form={commentForm} onFinish={handleAddComment} scrollToFirstError>
                  <Form.Item
                    name="content"
                    rules={[{ required: true, message: '' }]}
                  >
                    <TextArea rows={2} placeholder={t('board:commentPlaceholder')} />
                  </Form.Item>
                  <Form.Item
                    name="commentAttachments"
                    valuePropName="fileList"
                    getValueFromEvent={(e: any) => e?.fileList}
                  >
                    <Upload {...uploadProps}>
                      <Button size="small" icon={<UploadOutlined />}>{t('board:uploadImage')}</Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={submitting} size="small">
                      {t('board:addComment')}
                    </Button>
                  </Form.Item>
                </Form>
              </>
            )}

            {/* Activity Timeline — only visible to TESTER */}
            {canSeeComments && selectedTask.activities && selectedTask.activities.length > 0 && (
              <>
                <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
                  <ClockCircleOutlined /> {t('board:activity')}
                </Title>
                <Timeline
                  items={selectedTask.activities.map(act => ({
                    children: (
                      <div>
                        <Text strong>{act.actorName}</Text>{' '}
                        <Text>{t(`board:actions.${act.action}`)}</Text>
                        {act.action === 'STATUS_CHANGED' && act.details && (
                          <Text type="secondary">
                            {' '}({t(`board:statuses.${act.details.from}`)} → {t(`board:statuses.${act.details.to}`)})
                          </Text>
                        )}
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {formatDate(act.createdAt)}
                        </Text>
                      </div>
                    )
                  }))}
                />
              </>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default Board;
